import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { clientService } from "../../utils/services/clientService";
import { useDarkMode } from "../context/darkModeContext";

const ThemeWrapper = ({ children }) => {
  const [clientDetails, setClientDetails] = useState(null);
  const clientId = localStorage.getItem("client_id");
  const { darkMode, colors } = useDarkMode();

  const fetchClientById = async () => {
    try {
      const client = await clientService.get(clientId);
      setClientDetails(client);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchClientById();
    }
  }, [clientId]);

  /* sync body attribute so CSS-variable selectors respond */
  useEffect(() => {
    document.body.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const theme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-input": {
              color: colors.textPrimary,
              backgroundColor: "transparent",
              "&:-webkit-autofill": {
                "-webkit-box-shadow": darkMode
                  ? "0 0 0 30px #1B1F2E inset !important"
                  : "0 0 0 30px white inset !important",
                "-webkit-text-fill-color": darkMode
                  ? "#E2E8F0 !important"
                  : "black !important",
                transition: "background-color 5000s ease-in-out 0s",
                WebkitBoxShadow: "0 0 0 1000px transparent inset",
                WebkitTextFillColor: colors.textPrimary,
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: colors.textPrimary,
            "&:-webkit-autofill": {
              "-webkit-box-shadow": darkMode
                ? "0 0 0 30px #1B1F2E inset !important"
                : "0 0 0 30px white inset !important",
              "-webkit-text-fill-color": darkMode
                ? "#E2E8F0 !important"
                : "black !important",
              transition: "background-color 5000s ease-in-out 0s",
              WebkitBoxShadow: "0 0 0 1000px transparent inset",
              WebkitTextFillColor: colors.textPrimary,
              caretColor: colors.textPrimary,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: colors.textPrimary,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: colors.textPrimary,
            "&:hover": {
              backgroundColor: darkMode ? "#252A3A" : undefined,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: darkMode ? "#E2E8F0" : undefined,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          outlined: {
            borderColor: darkMode ? "#2C3045" : undefined,
            color: darkMode ? "#E2E8F0" : undefined,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: darkMode ? "#2C3045" : undefined,
            color: darkMode ? "#E2E8F0" : undefined,
          },
          head: {
            backgroundColor: darkMode ? "#242938" : "#f2f2f2",
            color: darkMode ? "#C8D0DC" : "#333333",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: darkMode ? "#242938" : "#f2f2f2",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            ...(darkMode && {
              "&:nth-of-type(even)": {
                backgroundColor: "#1F2433",
              },
              "&:nth-of-type(odd)": {
                backgroundColor: "#1B1F2E",
              },
            }),
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: darkMode ? "#8892A4" : undefined,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: {
            backgroundColor: darkMode ? "#2C3045" : undefined,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "& .MuiTab-root": {
              color: darkMode ? "#8892A4" : undefined,
            },
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            "& .MuiStepLabel-label": {
              color: darkMode ? "#E2E8F0" : undefined,
            },
          },
        },
      },
    },
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#6C9FFF" : clientDetails?.primary_color || "#DC585C",
      },
      secondary: {
        main: darkMode
          ? "#8892A4"
          : clientDetails?.secondary_color || "#7C7C7C",
      },
      ...(darkMode
        ? {
            background: {
              default: "#141824",
              paper: "#1B1F2E",
            },
            text: {
              primary: "#E2E8F0",
              secondary: "#8892A4",
            },
            divider: "#2C3045",
          }
        : {
            text: {
              primary: clientDetails?.text_color || "#000",
            },
          }),
      success: {
        main: "#4caf50",
      },
      warning: {
        main: "#ff9800",
      },
      info: {
        main: "#2196f3",
      },
      error: {
        main: "#f44336",
      },
      grey: {
        main: "#9e9e9e",
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default ThemeWrapper;
